import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import LoadingSpin from "react-loading-spin";

import { Table } from "../Table";
import { TableSkeleton } from "../TableSkeleton";
import {
  InfiniteTableContainer,
  LoadMoreButton,
  LoadingMoreContainer,
  EndOfListMessage,
} from "./InfiniteTable.styles";

interface IInfiniteTableProps<T> {
  data: T[];
  columns: ColumnDef<any, any>[];
  isLoading?: boolean;
  isError?: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onRowClick?: (rowContent: T) => void;
  emptyStateText?: string;
  highlightColor?: string | string[];
  highlightedRows?: boolean[];
  rowPadding?: boolean;
  fontSize?: string | number;
  fontWeight?: string | number;
  hasBorder?: boolean;
  showEndMessage?: boolean;
  endMessageText?: string;
  skeletonColumns?: number;
  skeletonRows?: number;
  loadMoreText?: string;
}

/**
 * InfiniteTable component that combines the Table component with pagination.
 * Shows a "Load More" button at the bottom when there are more items to load.
 *
 * @example
 * ```tsx
 * const { journals, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteJournals(patientId);
 *
 * <InfiniteTable
 *   data={journals}
 *   columns={columns}
 *   hasNextPage={hasNextPage}
 *   isFetchingNextPage={isFetchingNextPage}
 *   fetchNextPage={fetchNextPage}
 *   onRowClick={handleRowClick}
 * />
 * ```
 */
export function InfiniteTable<T>({
  data,
  columns,
  isLoading = false,
  isError = false,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRowClick,
  emptyStateText,
  highlightColor,
  highlightedRows,
  rowPadding,
  fontSize,
  fontWeight,
  hasBorder = true,
  showEndMessage = false,
  endMessageText,
  skeletonColumns = 4,
  skeletonRows = 8,
  loadMoreText,
}: IInfiniteTableProps<T>) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <InfiniteTableContainer>
        <TableSkeleton
          columnsNumber={skeletonColumns}
          rowsNumber={skeletonRows}
        />
      </InfiniteTableContainer>
    );
  }

  if (isError) {
    return (
      <InfiniteTableContainer>
        <p>{t("table_loadError")}</p>
      </InfiniteTableContainer>
    );
  }

  const hasData = data && data.length > 0;

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <InfiniteTableContainer hasBorder={hasBorder}>
      <Table
        data={data}
        columns={columns}
        onRowClick={onRowClick}
        emptyStateText={emptyStateText}
        highlightColor={highlightColor}
        highlightedRows={highlightedRows}
        rowPadding={rowPadding}
        fontSize={fontSize}
        fontWeight={fontWeight}
        hasBorder={false}
      />

      {/* Load More button */}
      {hasData && hasNextPage && (
        <LoadingMoreContainer>
          {isFetchingNextPage ? (
            <LoadingSpin
              size="24px"
              width="3px"
              primaryColor="var(--colors-portal-loading-state-color)"
              secondaryColor="#E8E8E8"
            />
          ) : (
            <LoadMoreButton onClick={handleLoadMore}>
              {loadMoreText ?? t("table_loadMore")}
            </LoadMoreButton>
          )}
        </LoadingMoreContainer>
      )}
      {hasData && !hasNextPage && showEndMessage && (
        <EndOfListMessage>
          {endMessageText ?? t("table_noMoreItems")}
        </EndOfListMessage>
      )}
    </InfiniteTableContainer>
  );
}
